import { z } from 'zod'

export const IncidentSeveritySchema = z.enum(['Low', 'Medium', 'High', 'Critical'])
export type IncidentSeverity = z.infer<typeof IncidentSeveritySchema>

export const IncidentStatusSchema = z.string()
export type IncidentStatus = z.infer<typeof IncidentStatusSchema>

export const IncidentAttachmentTypeSchema = z.enum(['Photo', 'Video', 'VoiceNote', 'File'])
export const IncidentFieldTypeSchema = z.enum(['Text', 'LongText', 'Number', 'YesNo', 'SingleSelect', 'MultiSelect', 'Date', 'Time'])

export const IncidentFormFieldSchema = z.object({
  key: z.string(),
  label: z.string(),
  fieldType: IncidentFieldTypeSchema,
  required: z.boolean(),
  options: z.array(z.string()).nullable(),
  helpText: z.string().nullable(),
  displayOrder: z.number(),
})

export const IncidentSubtypeSchema = z.object({
  id: z.number(),
  code: z.string(),
  name: z.string(),
  defaultSeverity: IncidentSeveritySchema,
  displayOrder: z.number(),
  fields: z.array(IncidentFormFieldSchema),
})

export const IncidentCategorySchema = z.object({
  id: z.number(),
  code: z.string(),
  name: z.string(),
  icon: z.string(),
  displayOrder: z.number(),
  subtypes: z.array(IncidentSubtypeSchema),
})

export const IncidentTaxonomyResponseSchema = z.object({
  version: z.string(),
  categories: z.array(IncidentCategorySchema),
})

export const IncidentListItemSchema = z.object({
  id: z.number(),
  categoryId: z.number(),
  categoryName: z.string(),
  categoryIcon: z.string(),
  subtypeId: z.number(),
  subtypeName: z.string(),
  severity: IncidentSeveritySchema,
  status: IncidentStatusSchema,
  description: z.string(),
  occurredAt: z.string(),
  submittedAt: z.string(),
  lastActivityAt: z.string(),
  shiftId: z.number().nullish(),
  siteId: z.number().nullish(),
  guardUserId: z.number().nullish(),
  reportedByClientUserId: z.number().nullish(),
  attachmentCount: z.number(),
  followUpCount: z.number(),
  number: z.number().nullish(),
  reference: z.string().nullish(),
  clientId: z.number().nullish(),
  clientName: z.string().nullish(),
  siteName: z.string().nullish(),
  reportedByName: z.string().nullish(),
})

export const PaginatedIncidentsResponseSchema = z.object({
  items: z.array(IncidentListItemSchema),
  totalCount: z.number(),
  pageNumber: z.number(),
  pageSize: z.number(),
  totalPages: z.number(),
})

export const IncidentAnswerViewSchema = z.object({
  key: z.string(),
  label: z.string(),
  fieldType: IncidentFieldTypeSchema,
  value: z.string(),
})

export const IncidentAttachmentViewSchema = z.object({
  id: z.number(),
  type: IncidentAttachmentTypeSchema,
  fileStorageId: z.string(),
  downloadUrl: z.string(),
  contentType: z.string(),
  fileSizeBytes: z.number(),
  uploadedAt: z.string(),
})

export const IncidentFollowUpViewSchema = z.object({
  id: z.number(),
  note: z.string(),
  addedByUserId: z.number(),
  addedByName: z.string().catch(''),
  addedAt: z.string(),
  attachments: z.array(IncidentAttachmentViewSchema),
})

export const IncidentStatusHistoryViewSchema = z.object({
  fromStatus: z.string().nullable(),
  toStatus: z.string(),
  changedByUserId: z.number(),
  changedByName: z.string().catch(''),
  changedAt: z.string(),
  note: z.string().nullable(),
})

export const IncidentDetailResponseSchema = z.object({
  id: z.number(),
  number: z.number().nullish(),
  reference: z.string().nullish(),
  reportRevision: z.number().nullish(),
  editedAt: z.string().nullish(),
  editedByName: z.string().nullish(),
  categoryId: z.number(),
  categoryName: z.string(),
  categoryIcon: z.string(),
  subtypeId: z.number(),
  subtypeName: z.string(),
  severity: IncidentSeveritySchema,
  status: IncidentStatusSchema,
  description: z.string(),
  answers: z.array(IncidentAnswerViewSchema),
  latitude: z.number().nullish(),
  longitude: z.number().nullish(),
  occurredAt: z.string(),
  submittedAt: z.string(),
  lastActivityAt: z.string(),
  shiftId: z.number().nullish(),
  siteId: z.number().nullish(),
  guardUserId: z.number().nullish(),
  clientId: z.number().nullish(),
  clientName: z.string().nullish(),
  siteName: z.string().nullish(),
  reportedByUserId: z.number().nullish(),
  reportedByName: z.string().nullish(),
  reportedByClient: z.boolean().nullish(),
  altitude: z.number().nullable().optional(),
  altitudeAccuracy: z.number().nullable().optional(),
  weatherCapturedAt: z.string().nullable().optional(),
  weatherCode: z.number().nullable().optional(),
  weatherTemperatureC: z.number().nullable().optional(),
  weatherPrecipitationMm: z.number().nullable().optional(),
  weatherVisibilityM: z.number().nullable().optional(),
  attachments: z.array(IncidentAttachmentViewSchema),
  followUps: z.array(IncidentFollowUpViewSchema),
  statusHistory: z.array(IncidentStatusHistoryViewSchema),
})

export const IncidentLocationSchema = z.object({
  id: z.number(),
  reference: z.string().nullish(),
  siteId: z.number().nullish(),
  latitude: z.number().nullish(),
  longitude: z.number().nullish(),
  severity: IncidentSeveritySchema,
  status: IncidentStatusSchema,
  categoryIcon: z.string(),
  subtypeName: z.string(),
  occurredAt: z.string(),
  subtypeCode: z.string().nullish(),
  guardUserId: z.number().nullish(),
  lastActivityAt: z.string().nullish(),
  acknowledgedAt: z.string().nullish(),
})

export const SOS_GUARD_SUBTYPE_CODE = 'SOS_PANIC'

export const IncidentLocationsResponseSchema = z.object({
  locations: z.array(IncidentLocationSchema),
  isTruncated: z.boolean(),
})

export const UpdateIncidentStatusRequestSchema = z.object({
  newStatus: z.string(),
  note: z.string().optional(),
})

export const UpdateIncidentStatusResponseSchema = z.object({
  id: z.number(),
  fromStatus: z.string(),
  toStatus: z.string(),
  changedAt: z.string(),
})

export const AddFollowUpRequestSchema = z.object({
  note: z.string().min(1).max(4000),
  attachmentIds: z.array(z.number()),
})

export const AddFollowUpResponseSchema = z.object({
  id: z.number(),
  addedAt: z.string(),
})

export type IncidentTaxonomy = z.infer<typeof IncidentTaxonomyResponseSchema>
export type IncidentCategory = z.infer<typeof IncidentCategorySchema>
export type IncidentSubtype = z.infer<typeof IncidentSubtypeSchema>
export type IncidentFormField = z.infer<typeof IncidentFormFieldSchema>
export type IncidentListItem = z.infer<typeof IncidentListItemSchema>
export type PaginatedIncidentsResponse = z.infer<typeof PaginatedIncidentsResponseSchema>
export type IncidentDetail = z.infer<typeof IncidentDetailResponseSchema>
export type IncidentLocation = z.infer<typeof IncidentLocationSchema>
export type LocatedIncident = IncidentLocation & { latitude: number; longitude: number }
export type IncidentLocationsResponse = z.infer<typeof IncidentLocationsResponseSchema>
export type IncidentAttachmentView = z.infer<typeof IncidentAttachmentViewSchema>
export type IncidentFollowUpView = z.infer<typeof IncidentFollowUpViewSchema>
export type IncidentStatusHistoryView = z.infer<typeof IncidentStatusHistoryViewSchema>
export type UpdateIncidentStatusRequest = z.infer<typeof UpdateIncidentStatusRequestSchema>
export type UpdateIncidentStatusResponse = z.infer<typeof UpdateIncidentStatusResponseSchema>
export type AddFollowUpRequest = z.infer<typeof AddFollowUpRequestSchema>
export type AddFollowUpResponse = z.infer<typeof AddFollowUpResponseSchema>

export function hasIncidentCoordinates(incident: IncidentLocation): incident is LocatedIncident {
  return incident.latitude != null && incident.longitude != null
}

export const IncidentReportHistorySchema = z.object({
  reference: z.string(),
  reportRevision: z.number(),
  original: z.object({ filedByName: z.string().nullish(), filedAt: z.string() }),
  revisions: z.array(z.object({ revisionNo: z.number(), editedByName: z.string().nullish(), editedAt: z.string() })),
  sends: z.array(z.object({
    id: z.number(),
    revisionNo: z.number(),
    recipients: z.array(z.string()),
    message: z.string().nullish(),
    sentByName: z.string().nullish(),
    sentAt: z.string(),
  })),
})
export type IncidentReportHistory = z.infer<typeof IncidentReportHistorySchema>

export const IncidentReportRevisionTextSchema = z.object({
  revisionNo: z.number(),
  description: z.string(),
  answers: z.array(IncidentAnswerViewSchema),
  occurredAt: z.string(),
})
export type IncidentReportRevisionText = z.infer<typeof IncidentReportRevisionTextSchema>

export const IncidentReportRecipientSchema = z.object({ name: z.string(), email: z.string() })
export type IncidentReportRecipient = z.infer<typeof IncidentReportRecipientSchema>

export interface EditIncidentReportBody {
  description: string
  answers: { key: string; value: string }[]
  occurredAt: string
  expectedRevision: number
}
